import { z } from 'zod';
import {
  createLocationCredentialId,
  type LocationCredential,
  type LocationCredentialSource,
  readStoredLocation,
  upsertLocationCredential,
} from '@/app/session/location-credentials';

export {
  LOCATION_CREDENTIAL_SOURCES,
  type LocationCredential,
  type LocationCredentialSource,
  maskLocationApiKey,
} from '@/app/session/location-credentials';

export interface InventoryUserSession {
  email: string;
  updatedAt: string;
}

const emailSchema = z
  .string()
  .transform(value => value.trim().toLowerCase())
  .pipe(z.string().email());
const storedUserSessionSchema = z.object({
  email: emailSchema,
});

export interface StartupSessionSnapshot {
  email: string;
  locations: LocationCredential[];
  parsedLocationApiKey: string;
  parsedLocationId: string | null;
  selectedLocationId: string | null;
}

const userSessionStorageKey = 'enmon-inventory-user-session';
const locationCredentialsStorageKey = 'enmon-inventory-location-credentials';
const selectedLocationStorageKey = 'enmon-inventory-selected-location-id';

export function readStartupSession(search = globalThis.location?.search ?? ''): StartupSessionSnapshot {
  const locations = readStoredLocations();
  const parsedApiKey = readLocationApiKeyFromSearch(search);
  const parsedLocationId = parsedApiKey ? createLocationCredentialId(parsedApiKey) : null;

  return {
    email: readStoredEmail(),
    locations,
    parsedLocationApiKey: parsedApiKey,
    parsedLocationId,
    selectedLocationId: readSelectedLocationId(locations),
  };
}

export function saveUserEmail(email: string, now = new Date().toISOString()) {
  const parsedEmail = emailSchema.safeParse(email);

  if (!parsedEmail.success) {
    throw new Error('Enter a valid email address.');
  }

  const session: InventoryUserSession = {
    email: parsedEmail.data,
    updatedAt: now,
  };

  globalThis.localStorage?.setItem(userSessionStorageKey, JSON.stringify(session));

  return session;
}

export function saveValidatedLocationApiKey(
  input: {
    apiKey: string;
    locationId?: number;
    locationName: string;
    source: LocationCredentialSource;
  },
  now = new Date().toISOString(),
) {
  const { apiKey, locationId, locationName, source } = input;
  const trimmedApiKey = apiKey.trim();

  if (!trimmedApiKey) {
    throw new Error('Enter a location API key.');
  }

  const locations = upsertLocationCredential(
    readStoredLocations(),
    { apiKey: trimmedApiKey, locationId, locationName, source },
    now,
  );
  const selectedLocationId = createLocationCredentialId(trimmedApiKey);

  writeStoredLocations(locations);
  saveSelectedLocationId(selectedLocationId);

  return {
    locations,
    selectedLocationId,
  };
}

export function selectLocationCredential(locationId: string) {
  const locations = readStoredLocations();
  const exists = locations.some(location => location.id === locationId);

  if (!exists) {
    throw new Error('Choose a saved location.');
  }

  saveSelectedLocationId(locationId);

  return locationId;
}

export function removeLocationCredential(locationId: string) {
  const locations = readStoredLocations().filter(location => location.id !== locationId);
  const selectedLocationId = readSelectedLocationId(locations);

  writeStoredLocations(locations);
  saveSelectedLocationId(selectedLocationId);

  return {
    locations,
    selectedLocationId,
  };
}

export function readLocationApiKeyFromSearch(search: string) {
  const params = new URLSearchParams(search);
  return (params.get('apiKey') ?? '').trim();
}

export function stripLocationApiKeySearchParams() {
  if (!(globalThis.location && globalThis.history)) {
    return;
  }

  const url = new URL(globalThis.location.href);

  if (!url.searchParams.has('apiKey')) {
    return;
  }

  url.searchParams.delete('apiKey');
  globalThis.history.replaceState(globalThis.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

export function isValidEmail(email: string) {
  return emailSchema.safeParse(email).success;
}

function readStoredEmail() {
  const rawValue = globalThis.localStorage?.getItem(userSessionStorageKey);

  if (!rawValue) {
    return '';
  }

  try {
    const parsedSession = storedUserSessionSchema.safeParse(JSON.parse(rawValue));
    return parsedSession.success ? parsedSession.data.email : '';
  } catch {
    return '';
  }
}

function readStoredLocations(): LocationCredential[] {
  const rawValue = globalThis.localStorage?.getItem(locationCredentialsStorageKey);

  if (!rawValue) {
    return [];
  }

  try {
    const values = z.array(z.unknown()).safeParse(JSON.parse(rawValue));

    if (!values.success) {
      return [];
    }

    return values.data.flatMap(readStoredLocation);
  } catch {
    return [];
  }
}

function writeStoredLocations(locations: LocationCredential[]) {
  globalThis.localStorage?.setItem(locationCredentialsStorageKey, JSON.stringify(locations));
}

function readSelectedLocationId(locations: LocationCredential[]) {
  const storedLocationId = globalThis.localStorage?.getItem(selectedLocationStorageKey);

  if (storedLocationId && locations.some(location => location.id === storedLocationId)) {
    return storedLocationId;
  }

  return locations[0]?.id ?? null;
}

function saveSelectedLocationId(locationId: string | null) {
  if (!locationId) {
    globalThis.localStorage?.removeItem(selectedLocationStorageKey);
    return;
  }

  globalThis.localStorage?.setItem(selectedLocationStorageKey, locationId);
}
